import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Loading, ErrorState, Empty, PageTitle } from "@/components/States";
import { AppointmentCard } from "@/components/AppointmentCard";
import { areaCls, btnPrimary, card } from "@/lib/ui";

const DisputeItem = ({ d, reload }) => {
  const [text, setText] = useState("");
  const post = async (path) => {
    try {
      await api.post(`/disputes/${d.id}/${path}`, { text });
      toast.success(path === "respond" ? "Response submitted for admin review" : "Appeal submitted");
      setText("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const canRespond = d.role === "accused" && d.status === "open" && !d.response;
  const canAppeal = d.role === "accused" && d.status === "substantiated" && !d.appeal;
  return (
    <div data-testid={`dispute-${d.id}`} className={card + " space-y-2"}>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm">{d.role === "reporter" ? `You reported ${d.accused?.first_name} as a no-show` : `${d.reporter?.first_name} reported you as a no-show`}</p>
        <span data-testid="dispute-status" className="text-xs font-bold uppercase text-blue-700">{d.status.replace("_", " ")}</span>
      </div>
      <p className="text-xs text-slate-500">Filed {fmtDate(d.created_at)}. A single report never affects reputation on its own — an admin reviews both sides.</p>
      {d.statement && <p className="text-sm"><b>Report:</b> {d.statement}</p>}
      {d.response && <p className="text-sm"><b>Response:</b> {d.response}</p>}
      {d.appeal && <p className="text-sm"><b>Appeal ({d.appeal.status}):</b> {d.appeal.text}</p>}
      {(canRespond || canAppeal) && (
        <>
          <textarea data-testid="dispute-text-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={canRespond ? "Share your side (e.g. I was there at 5pm, messaged at 5:10)" : "Why should this decision be reconsidered?"} className={areaCls} />
          <button data-testid={canRespond ? "dispute-respond-btn" : "dispute-appeal-btn"} disabled={text.trim().length < 2} onClick={() => post(canRespond ? "respond" : "appeal")} className={btnPrimary + " h-10"}>{canRespond ? "Submit response" : "Submit appeal"}</button>
        </>
      )}
    </div>
  );
};

export default function Pickups() {
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    const req = tab === "disputes" ? api.get("/disputes/mine") : api.get("/appointments", { params: { scope: tab === "upcoming" ? "upcoming" : "all" } });
    req.then(({ data }) => setItems(data)).catch((e) => setErr(errMsg(e)));
  }, [tab]);
  useEffect(() => {
    setItems(null);
    load();
  }, [load]);
  return (
    <div className="max-w-3xl">
      <PageTitle sub="Confirmed details, reminders and outcomes in one place.">Pickups</PageTitle>
      <div className="flex gap-1 p-1 rounded-xl bg-slate-100 w-fit mb-5">
        {[["upcoming", "Upcoming"], ["all", "History"], ["disputes", "Disputes"]].map(([k, v]) => (
          <button key={k} data-testid={`pickups-tab-${k}`} onClick={() => setTab(k)} className={`px-4 h-9 rounded-lg text-sm font-semibold ${tab === k ? "bg-white shadow-sm" : "text-slate-500"}`}>{v}</button>
        ))}
      </div>
      {err ? <ErrorState message={err} onRetry={load} /> : !items ? <Loading /> : items.length === 0 ? (
        <Empty title={tab === "disputes" ? "No disputes" : "No pickups here"} body={tab === "upcoming" ? "Schedule a pickup from any listing to see it here." : undefined} />
      ) : tab === "disputes" ? (
        <div className="space-y-3">{items.map((d) => <DisputeItem key={d.id} d={d} reload={load} />)}</div>
      ) : (
        <div className="space-y-5">
          {items.map((a) => (
            <div key={a.id} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <Link to={`/listing/${a.listing_id}`} className="font-bold text-slate-900 truncate">{a.listing?.title}</Link>
                <Link to={`/inbox/${a.conversation_id}`} data-testid={`pickup-chat-${a.id}`} className="text-blue-600 font-semibold shrink-0">Open chat</Link>
              </div>
              <p className="text-xs text-slate-500">{a.role === "buyer" ? "Buying from" : "Selling to"} {a.other_user?.first_name}</p>
              <AppointmentCard appt={a} onChange={load} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
